import { useEffect, useRef, useState } from 'react';
import { usePlacesWidget } from 'react-google-autocomplete';
import { useFormContext, useWatch } from 'react-hook-form';
import { useSelector } from 'react-redux';
import {
  paramsAdd,
  paramsEdit,
  hiddenField,
  additionalPhonesCategory,
  sOption
} from '../constants/form';
import { mediaPNG, fileStatus } from '../constants/files';
import { envSetting } from '../constants/envSetting';
import { baseField } from '../constants/base';
import {
  assignDate,
  fieldAttribute,
  fieldUniqueName,
  isEmpty,
  isUndefinedNull,
  notUndefinedNull,
  sendFormatDate,
  setMask,
  trimLowerCase,
  getUniqueName,
} from '../helpers/shared';
import { sendData, dataURItoBlob } from '../helpers/base';
import { actionTextInput } from '../helpers/form';
import numeral from 'numeral';
import { useParams } from 'react-router-dom';
import { useTextLanguage } from './useShared';
import { mainDocument } from '../constants/files';

/**
 * use to SignatureInput
 * @param {Function} setValue
 * @param {String} uniqueName
 * @returns
 */
export const useSignature = (setValue, uniqueName, isNewDesign) => {
  const signature = useRef({});
  const [dataURL, setDataURL] = useState('');
  const [dataBase, setDataBase] = useState('');

  const applyBackground = (color = '#f5f5f5') => {
    const canvas = signature?.current?.getCanvas?.();
    if (!canvas) return;
    canvas.style.backgroundColor = color;
    canvas.style.borderRadius = '10px'
  };


  useEffect(() => {
    if (isNewDesign) applyBackground()
  }, []);

  useEffect(() => {
    setDataURL(signature?.current?.toDataURL(mediaPNG));
  }, [dataURL, signature]);

  const clear = () => {
    signature.current.clear();
    setValue(uniqueName, '');
  };

  const handleEnd = () => {
    const dataUrl = signature?.current?.toDataURL(mediaPNG);
    setDataBase(dataUrl);
    const file = dataURItoBlob(dataUrl, `digital_sign.png`);
    setValue(uniqueName, file);
  };

  return { clear, dataBase, handleEnd, signature };
};

/**
 * hook to TextInput component
 * @param {Object} field
 * @param {String} uniqueName
 * @returns
 */
export const useTextInput = (field, uniqueName, isGroup) => {
  const optionType = new Set(['money', 'url']);
  const type = (optionType.has(trimLowerCase(
    field.data_type))) ? 'text' : trimLowerCase(field.data_type);
  const { defaultValuesTier, action } = useSelector(state => state.tier);
  const { setValue, getValues } = useFormContext();
  const values = getValues();
  const [maskLength, setMaskLength] = useState(null)
  const [validate, setValidate] = useState(null)
  /* Implementing date_auto_option 'type' */
  const { languageText } = useTextLanguage();
  const { field: fields, document_type } = useSelector(state => state?.staticData[`data${languageText}`])
  const functionGetValues = field?.function_get_values ? JSON.parse(field.function_get_values) : null
  const fields_to_depend = functionGetValues ? functionGetValues.fields_to_depend : undefined;
  const dependendantField = fields.filter(field => field?.unique_name === fields_to_depend?.depends)[0]
  const monthlyFieldName = fields.filter(field => field.unique_name === fields_to_depend?.options[0]?.baseField)[0]
  const bimonthlyFieldName = fields.filter(field => field.unique_name === fields_to_depend?.options[1]?.baseField)[0]
  const biweeklyFieldName = fields.filter(field => field.unique_name === fields_to_depend?.options[2]?.baseField)[0]
  const autoDateDependsOn = useWatch({name: getUniqueName(dependendantField) || ''})
  const monthlyVal = useWatch({name: getUniqueName(monthlyFieldName) || ''})
  const bimonthlyVal = useWatch({name: getUniqueName(bimonthlyFieldName) || ''})
  const biweeklyVal = useWatch({name: getUniqueName(biweeklyFieldName) || ''})

  // ? For differentiating documents
  const group = uniqueName.split('__')[0];
  const isDocument = mainDocument.includes(group)
  const documentType = document_type?.find(doc => (trimLowerCase(doc.code) === group)) || ''

  useEffect(() => {
    const mask = (notUndefinedNull(field.mask)) ? JSON.parse(field.mask) : '';

    if (mask?.apply === 'start') setMask(fieldUniqueName(
      field.unique_name), paramsAdd, field.mask);
  }, [defaultValuesTier, field]);

  useEffect(() => {
    const maskProp = field?.mask ? JSON.parse(field?.mask) : null

    setTimeout(() => {
      if (maskProp && maskProp?.min_length) {
        setMaskLength(Number(maskProp?.min_length))
        setValidate('minLength')
      } 
    }, 1000)
  }, [values])

  useEffect(() => {
    if (notUndefinedNull(values[uniqueName]) && !isEmpty(values[uniqueName])) {
      setValue(uniqueName, values[uniqueName]);
    } else {
      if (notUndefinedNull(field.default_value)){
        const defaultValue = JSON.parse(field.default_value);
        if (notUndefinedNull(defaultValue?.text) && (notUndefinedNull(action) || isGroup)) {
          setValue(uniqueName, defaultValue?.text);
        } 
      }
    }
  }, []);

  const { areDocumentsFetched, documentsFetched } = useSelector(state => state.base)
  // * Fix for re-filling the fields included in the 'required' documents, answer of the 'documents-condition' endpoint.
  useEffect(() => {
    if (documentsFetched?.some(doc => uniqueName.includes(doc))) { 
      setTimeout(() => {
        const currentValue = getValues(uniqueName);
        const snapshotValue = values?.[uniqueName];
        const hasLiveValue = notUndefinedNull(currentValue) && currentValue !== '' &&
          currentValue !== hiddenField;
        if (!hasLiveValue && snapshotValue !== undefined && snapshotValue !== hiddenField) {
          setValue(uniqueName, snapshotValue);
        }
      }, 100);
    } 
  }, [areDocumentsFetched, documentsFetched, documentsFetched?.length]);

  // * Feat for fields whose 'functionGetValues.type' is equal to 'date_auto_option' 
  useEffect(() => {
    if (functionGetValues?.type === 'date_auto_option' && autoDateDependsOn && values) {
      
      let updatedValues = getValues()
      const response = sendData(functionGetValues?.type, functionGetValues, false, setValue, autoDateDependsOn, updatedValues)
      if (response && trimLowerCase(response) !== 'nan/nan/nan') {
        let date = new Date(response)
        const formattedResponse = sendFormatDate(field, 'send', date) 
        setValue(getUniqueName(field), formattedResponse)
      } else  {
        setValue(getUniqueName(field), hiddenField)
      }
    }
  }, [autoDateDependsOn, monthlyVal, bimonthlyVal, values, functionGetValues, biweeklyVal])

  return { type, maskLength, validate, isDocument, documentType };
};

/**
 * hook to Input component
 * @param {Object} field
 * @param {String} type
 * @param {Function} setValue
 * @returns
 */
export const useInput = (field, type, setValue, entryProps) => {
  const debounceRef = useRef(null);
  const lastValidatedValueRef = useRef('');
  const fieldAction = (field?.action) ? JSON.parse(field.action) : {};

  const onKeyDown = event => {
    if (type === 'number') {
      const invalidChars = [
        '-',
        '+',
        'e',
        '.'
      ];
      if (invalidChars.includes(event.key)) {
        event.preventDefault();
      }
    }
  };

  // Currently only being used in fields with action.validate ('fieldActions.validate')
  const onKeyUp = ({ target }) => {
    const targetValue = target?.value || '';

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(async () => {
      let value = targetValue;
      /* Remove value from their '-' and '_'s */
      value = value.replace(/[-_]/g, '');

      if (value.length >= field.min_length && field.action && lastValidatedValueRef.current !== value) {
        lastValidatedValueRef.current = value;
        await actionTextInput(field, target, value, setValue);
      }

      if(entryProps?.category === additionalPhonesCategory){
        const { setCanAddPhone, fields, getValues } = entryProps
        const minLength = field.min_length ? field.min_length : 6
        /* We need the values of this entry's country field. */
        const countryField = fields.filter(field => field?.unique_name?.includes('country'))[0]
        const countryName = getUniqueName(countryField)
        const countryNameValue = getValues(countryName)
        const countryHasValue = countryNameValue && (countryNameValue !== hiddenField) && (countryNameValue !== sOption) && notUndefinedNull(countryNameValue)

        if (targetValue.length >= minLength && countryHasValue) {
          setCanAddPhone(true)
        } else {
          setCanAddPhone(false)
        }
      }
    }, 350);
  };

  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  const onClick = ({ target }, name) => {
    const input = fieldAttribute('name', name);
    target.classList.add('d-none');

    if (target.classList.contains('img-eye-open')) {
      const eyeClosed = fieldAttribute('data-password', name, '.img-eye-closed');
      eyeClosed.classList.remove('d-none');
    } else {
      const eyeOpen = fieldAttribute('data-password', name, '.img-eye-open');
      eyeOpen.classList.remove('d-none');
    }

    if (input.type === 'password') {
      input.type = 'text';
    } else {
      input.type = 'password';
    }
  };

  return { fieldAction, onClick, onKeyDown, onKeyUp };
};

/**
 * hook to MoneyInput Component
 * @param {Object} field
 * @param {String} uniqueName
 * @returns
 */
export const useMoneyInput = (field, uniqueName) => {
  const type = (trimLowerCase(
    field.data_type) === 'number') ? 'text' : trimLowerCase(field.data_type);
  const { setValue, getValues } = useFormContext();
  let value = useWatch({ name: uniqueName });
  const { requestLanguage, currency } = useSelector(state => state.base);
  const { rates, field: fields } = useSelector(state => {
    const data = state.staticData[`data_${trimLowerCase(requestLanguage)}`];
    return data || {};
  });
  const action = field?.action ? JSON.parse(field.action) : ''

  const updateConversion = (sourceField, sourceValue) => {
    const conversionFieldName = `${sourceField?.unique_name}_conversion`
    let labelConversion = fieldAttribute(
      'data-unique-name',
      `${sourceField?.unique_name}_conversion`,
      'label'
    )

    // ? Aditional logic for sform_tier
    if (!labelConversion && conversionFieldName.includes('_sform')) {
      labelConversion = fieldAttribute(
        'data-unique-name',
        `${sourceField.unique_name.slice(0, sourceField.unique_name.indexOf('_sform'))}_conversion_sform`,
        'label'
      )
    }

    if (!labelConversion || labelConversion?.dataset?.parent == null) return

    let conversionValue = String(sourceValue ?? '0')
    const parentField = fields.find(currentField => (
      trimLowerCase(currentField.id) === trimLowerCase(labelConversion.dataset.parent)
    ))
    const parent = parentField && fieldAttribute(
      'name',
      `${parentField.unique_name}__id__${parentField.id}`
    )

    if (!parent) return

    const codeParent = parent.options[parent.selectedIndex]?.dataset?.code
    const name = labelConversion.getAttribute('name')

    if (!isEmpty(conversionValue) && rates?.[codeParent]) {
      conversionValue = (Number.parseFloat(conversionValue) /
        Number.parseFloat(rates[codeParent].rate)).toFixed(0)
      conversionValue = trimLowerCase(conversionValue) === 'nan' ? '0' : conversionValue
    }

    const labelMask = sourceField?.mask ? JSON.parse(sourceField.mask) : null
    if (!isEmpty(conversionValue) && labelMask) {
      const finalValue = numeral(conversionValue).format(
        `0${labelMask.group}0${labelMask.decimal}`
      )
      labelConversion.textContent = `${currency.code} ${finalValue}`
    } else {
      labelConversion.textContent = '0,00'
    }

    setValue(name, conversionValue)
  }

  const onChange = event => {
    if (event?.type === 'change') {
      let value = event.target.value.replaceAll(',', '').replaceAll('.', '');

      value = (parseInt(value) >= 0) ? value : '0';
      setValue(uniqueName, value);

      value = (isUndefinedNull(value) || isEmpty(value)) ? '0' : value;

      if (event?.relatedTarget?.type !== 'select-one') {
        updateConversion(field, value)
      }

      // * Feat for action.validate = 'multiply'
      if (action?.times && action?.field) {
        try {
          const childName = Object.keys(getValues()).find(field => field.startsWith(action?.field))
          if (childName) {
            const childValue = Number(value) * Number(action?.times)
            const childField = fields.find(currentField => (
              childName.startsWith(`${currentField.unique_name}__id__`)
            ))

            setValue(childName, childValue)
            if (childField) updateConversion(childField, childValue)
          }
        } catch (error) {
          console.error(error)
        }
      }
    }
  };

  value = value == null ? '' : String(value).replace(/[,.]/g, '')

  return { 
    onChange, 
    type, 
    value: Number(value),
    action
  };
};

/**
 * hook to DateInput Component
 * @param {Object} field
 * @param {String} uniqueName
 * @returns
 */
const isValidDate = date => (date instanceof Date && !Number.isNaN(date.getTime()));

export const useDateInput = (field, uniqueName, isGroup = false) => {
  const { setValue, getValues } = useFormContext();
  const { action } = useSelector(state => state.tier);
  const [selectedDay, setSelectedDay] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  let values = getValues(uniqueName);
  values = Array.isArray(values) ? values?.[0] : values
  const { codeTier } = useParams()

  const { areDocumentsFetched, documentsFetched } = useSelector(state => state.base)
  // * Fix for re-filling the fields included in the 'required' documents, answer of the 'documents-condition' endpoint.
  useEffect(() => {
    if (documentsFetched?.some(doc => uniqueName.includes(doc))) { 
      setTimeout(() => {
        const currentValue = getValues(uniqueName);
        const snapshotValue = values;
        const hasLiveValue = notUndefinedNull(currentValue) && currentValue !== '' &&
          currentValue !== hiddenField;
        if (!hasLiveValue && snapshotValue !== undefined && snapshotValue !== hiddenField) {
          setValue(uniqueName, snapshotValue);
          const dateElem = document.getElementsByName(uniqueName)[0]
          dateElem.value = snapshotValue
        }
      }, 100);
    } 
  }, [areDocumentsFetched, documentsFetched, documentsFetched?.length]);

  const onChange = date => {
    if (date && isValidDate(date)) {
      setSelectedDay(date);
      const formattedDate = sendFormatDate(field, 'send', date);
      const fieldMask = (notUndefinedNull(field.mask)) ? JSON.parse(
        field.mask) : null;
      let newDate;

      if (notUndefinedNull(fieldMask) && !fieldMask?.set_hour) {
        newDate = formattedDate;
      } else if (notUndefinedNull(fieldMask) && fieldMask?.set_hour) {
        newDate = formattedDate ? new Date(`${formattedDate} 00:00:00`) : '';
      } else {
        newDate = date;
      }
      setValue(uniqueName, JSON.stringify(newDate));
    }
  };

  useEffect(() => {
    setTimeout(() => {
      let values = getValues(uniqueName);
      values = Array.isArray(values) ? values?.[0] : values
      if ((action === paramsEdit || action === paramsAdd || codeTier === fileStatus.rejected || codeTier === fileStatus.expired) && (values || isGroup)) {
        if (values !== hiddenField && notUndefinedNull(values) && values) {
          let parsedValue = typeof values === "string" ? values : JSON.parse(values);
          parsedValue = parsedValue.replace(/^"|"$/g, ''); // Remove quotes
          const valueToDate = new Date(parsedValue);
          if (isValidDate(valueToDate)) {
            const date = {
              day: valueToDate.getDate(),
              month: valueToDate.getMonth() + 1,
              year: valueToDate.getFullYear()
            };
            if (notUndefinedNull(date)) {
              setSelectedDay(new Date(
                date.year,
                date.month - 1,
                date.day
              ));
            }
          }

        } else {
          if (notUndefinedNull(field.default_value) && (codeTier === fileStatus.rejected || codeTier === fileStatus.expired)) {
            const defaultValue = JSON.parse(field.default_value);
            if (notUndefinedNull(defaultValue?.text)) {
              setValue(uniqueName, [defaultValue?.text]);
            } 
          }
        }
      } else if (action === paramsAdd) {
        if (notUndefinedNull(selectedDay) && isValidDate(selectedDay)) onChange(selectedDay);
      }
    }, 20);
  }, [action, field, uniqueName, values, codeTier]);

  useEffect(() => {
    setStartDate(assignDate(field, baseField.startDate));
    setEndDate(assignDate(field, baseField.endDate));
  }, [field]);

  const formatInputValue = () => {
    if (!selectedDay || !isValidDate(selectedDay)) return '';

    return (sendFormatDate(field, 'format', selectedDay));
  };

  return { endDate, formatInputValue, onChange, selectedDay, startDate };
};

/**
 * hook to get addresses for autocomplete
 * @param {Function} setValue
 * @param {String} uniqueName
 * @returns
 */
export const useAutocompleteGeolocation = (
  setValue,
  uniqueName
) => {
  const { location, actualLanguage } = useSelector(state => state.base);
  const apiKey = envSetting.googleAutocompleteApiKey;
  let isSelectedPlace = false;

  const { ref } = usePlacesWidget({
    apiKey: apiKey,
    onPlaceSelected: (place) => {
      isSelectedPlace = true;
      setValue(uniqueName, place.formatted_address);
    },
    options: {
      types: ["address"],
      fields: ["formatted_address"],
      componentRestrictions: { country: location },
    },
    language: trimLowerCase(actualLanguage)
  });

  const setValueOnNotSelected = event => {
    if (!isSelectedPlace && notUndefinedNull(apiKey)) {
      setValue(uniqueName, event.target.value);
    } else if (isEmpty(event.target.value)) {
      isSelectedPlace = false;
    }
  };

  return { ref, apiKey, setValueOnNotSelected };
};